import type { BondPayload, BondStatus, ContractKind, PersonStatus } from '../api';

/** Rótulos e regras de tela do cadastro mestre. Funções puras, testadas. */

export const BOND_STATUS_LABELS: Record<BondStatus, string> = {
  admissao_em_preparacao: 'Admissão em Preparação',
  aguardando_documentos: 'Aguardando Documentos',
  aguardando_exame_admissional: 'Aguardando Exame Admissional',
  aguardando_contabilidade: 'Aguardando Contabilidade',
  aguardando_assinatura: 'Aguardando Assinatura',
  admissao_confirmada: 'Admissão Confirmada',
  ativo: 'Ativo',
  em_experiencia: 'Em Experiência',
  afastado: 'Afastado',
  licenciado: 'Licenciado',
  ferias: 'Férias',
  aviso_previo: 'Aviso Prévio',
  desligamento_em_processamento: 'Desligamento em Processamento',
  desligado: 'Desligado',
  contratacao_cancelada: 'Contratação Cancelada',
  transferido: 'Transferido',
  suspenso: 'Suspenso',
  em_treinamento: 'Em Treinamento',
  licenca_medica: 'Licença Médica',
  licenca_maternidade: 'Licença Maternidade',
  licenca_paternidade: 'Licença Paternidade',
  licenca_nao_remunerada: 'Licença Não Remunerada',
  aposentado: 'Aposentado',
  falecido: 'Falecido',
};

export type StatusTone = 'green' | 'blue' | 'amber' | 'gray' | 'red';

export function bondStatusTone(status: BondStatus): StatusTone {
  if (status === 'ativo' || status === 'em_experiencia') return 'green';
  if (['desligado', 'contratacao_cancelada', 'transferido', 'aposentado', 'falecido'].includes(status)) return 'gray';
  if (status === 'suspenso' || status === 'aviso_previo' || status === 'desligamento_em_processamento') return 'red';
  if (status === 'afastado' || status === 'licenciado' || status === 'ferias' || status.startsWith('licenca_') || status === 'em_treinamento') {
    return 'blue';
  }
  return 'amber';
}

export const PERSON_STATUS_LABELS: Record<PersonStatus, string> = {
  pre_cadastrada: 'Pré-cadastrada',
  em_validacao: 'Em validação',
  validada: 'Validada',
  bloqueada: 'Bloqueada',
  inativa: 'Inativa',
  falecida: 'Falecida',
};

export const CONTRACT_KIND_LABELS: Record<ContractKind, string> = {
  indeterminado: 'Prazo indeterminado',
  determinado: 'Prazo determinado',
  experiencia: 'Experiência',
};

export const ORIGIN_LABELS: Record<string, string> = {
  manual: 'Inclusão manual',
  candidato: 'Candidato aprovado',
  recontratacao: 'Recontratação',
  migracao: 'Migração',
};

export const SEX_OPTIONS = [
  { value: 'feminino', label: 'Feminino' },
  { value: 'masculino', label: 'Masculino' },
];

export const MARITAL_OPTIONS = [
  { value: 'solteiro', label: 'Solteiro(a)' },
  { value: 'casado', label: 'Casado(a)' },
  { value: 'uniao_estavel', label: 'União estável' },
  { value: 'separado', label: 'Separado(a)' },
  { value: 'divorciado', label: 'Divorciado(a)' },
  { value: 'viuvo', label: 'Viúvo(a)' },
];

export const EDUCATION_OPTIONS = [
  { value: 'fundamental_incompleto', label: 'Fundamental incompleto' },
  { value: 'fundamental_completo', label: 'Fundamental completo' },
  { value: 'medio_incompleto', label: 'Médio incompleto' },
  { value: 'medio_completo', label: 'Médio completo' },
  { value: 'superior_incompleto', label: 'Superior incompleto' },
  { value: 'superior_completo', label: 'Superior completo' },
  { value: 'pos_graduacao', label: 'Pós-graduação' },
  { value: 'mestrado', label: 'Mestrado' },
  { value: 'doutorado', label: 'Doutorado' },
];

export const HISTORY_EVENT_LABELS: Record<string, string> = {
  created: 'Vínculo criado',
  status_changed: 'Situação alterada',
  updated: 'Contrato alterado',
  exported: 'Admissão exportada para a contabilidade',
  payroll_registration_set: 'Matrícula da folha registrada',
  payroll_divergence: 'Divergência da contabilidade',
  reconciled: 'Divergência conciliada',
};

export function labelOf(options: Array<{ value: string; label: string }>, value: string | null | undefined): string {
  if (!value) return '—';
  return options.find((o) => o.value === value)?.label ?? value;
}

/** `2026-09-01` → `01/09/2026` sem passar por `Date` (data civil, sem fuso). */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const [y, m, d] = value.slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : value;
}

/** Nacionalidade brasileira exige naturalidade (mesma regra do backend). */
export function isBrazilian(nationality: string | null | undefined): boolean {
  return !!nationality && /^brasileir/i.test(nationality.trim());
}

const MAX_PROBATION_DAYS = 90;

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/**
 * Validação do vínculo no formulário, espelhando `contract-rules.ts` do
 * backend para o usuário ver o erro antes de salvar. O backend continua
 * sendo a autoridade.
 */
export function validateBondForm(bond: Partial<BondPayload>): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!bond.companyId) errors.companyId = 'Selecione a empresa.';
  if (!bond.employmentType) errors.employmentType = 'Selecione o tipo de vínculo.';
  if (!bond.admissionDate) errors.admissionDate = 'Informe a data de admissão.';
  if (!bond.contractKind) errors.contractKind = 'Selecione o tipo de contrato.';
  const admission = bond.admissionDate;

  if (bond.contractKind === 'determinado') {
    if (!bond.contractEndDate) errors.contractEndDate = 'Informe a data final prevista.';
    else if (admission && bond.contractEndDate <= admission) {
      errors.contractEndDate = 'A data final deve ser posterior à admissão.';
    }
  }
  if (bond.contractKind === 'experiencia') {
    if (bond.employmentType === 'pj') errors.contractKind = 'Experiência só se aplica a vínculo CLT.';
    if (!bond.probationEndDate) errors.probationEndDate = 'Informe o fim da experiência.';
    else if (admission) {
      if (bond.probationEndDate <= admission) errors.probationEndDate = 'O fim da experiência deve ser posterior à admissão.';
      if (bond.probationExtensionEndDate && bond.probationExtensionEndDate <= bond.probationEndDate) {
        errors.probationExtensionEndDate = 'A prorrogação deve terminar depois do fim inicial.';
      }
      const last = bond.probationExtensionEndDate || bond.probationEndDate;
      if (daysBetween(admission, last) + 1 > MAX_PROBATION_DAYS) {
        errors[bond.probationExtensionEndDate ? 'probationExtensionEndDate' : 'probationEndDate'] =
          `A experiência não pode passar de ${MAX_PROBATION_DAYS} dias.`;
      }
    }
  }
  return errors;
}

/** Remove datas que não se aplicam ao tipo de contrato antes de enviar. */
export function cleanBondPayload(bond: BondPayload): BondPayload {
  const out: BondPayload = {
    companyId: bond.companyId,
    employmentType: bond.employmentType,
    admissionDate: bond.admissionDate,
    contractKind: bond.contractKind,
  };
  if (bond.contractKind === 'determinado' && bond.contractEndDate) out.contractEndDate = bond.contractEndDate;
  if (bond.contractKind === 'experiencia') {
    if (bond.probationEndDate) out.probationEndDate = bond.probationEndDate;
    if (bond.probationExtensionEndDate) out.probationExtensionEndDate = bond.probationExtensionEndDate;
  }
  if (bond.internalRegistration?.trim()) out.internalRegistration = bond.internalRegistration.trim();
  return out;
}

// --- Documentos, endereços e contas (change `pessoas-documentos-contatos-bancos`) ---

export const DOCUMENT_CATEGORY_LABELS: Record<string, string> = {
  pessoal: 'Pessoais',
  trabalhista: 'Trabalhistas',
  bancario: 'Bancários',
  escolaridade: 'Escolaridade e qualificação',
  dependentes: 'Dependentes',
  beneficios: 'Benefícios',
  ocupacional: 'Saúde e segurança',
  funcional: 'Funcionais',
  afastamento: 'Afastamentos',
  desligamento: 'Desligamento',
};

export const CONFIDENTIALITY_LABELS: Record<string, string> = {
  interno: 'Interno',
  restrito: 'Restrito',
  bancario: 'Bancário',
  medico: 'Médico',
};

export const REJECTION_REASON_OPTIONS = [
  { value: 'ilegivel', label: 'Ilegível' },
  { value: 'incompleto', label: 'Incompleto' },
  { value: 'vencido', label: 'Vencido' },
  { value: 'documento_incorreto', label: 'Documento incorreto' },
  { value: 'dados_divergentes', label: 'Dados divergentes' },
  { value: 'sem_assinatura', label: 'Ausência de assinatura' },
  { value: 'arquivo_invalido', label: 'Arquivo inválido' },
  { value: 'falta_frente_verso', label: 'Falta de frente ou verso' },
  { value: 'documento_de_terceiro', label: 'Documento de terceiro' },
  { value: 'comprovacao_insuficiente', label: 'Comprovação insuficiente' },
  { value: 'autenticidade_nao_confirmada', label: 'Autenticidade não confirmada' },
];

/** Situações em que o documento pode receber nova versão (espelha o backend). */
const REPLACEABLE = new Set(['validado', 'validado_com_ressalva', 'vencimento_proximo', 'vencido', 'rejeitado', 'correcao_solicitada']);
const AWAITING = new Set(['recebido', 'em_validacao']);
const LOCKED = new Set(['validado', 'validado_com_ressalva', 'vencimento_proximo', 'vencido', 'substituido']);

export const canReplaceDocument = (status: string) => REPLACEABLE.has(status);
export const awaitsReview = (status: string) => AWAITING.has(status);
export const canCancelDocument = (status: string) => !LOCKED.has(status) && status !== 'cancelado';

export function documentStatusTone(status: string): StatusTone {
  if (status === 'validado' || status === 'validado_com_ressalva') return 'green';
  if (status === 'rejeitado' || status === 'vencido') return 'red';
  if (status === 'substituido' || status === 'cancelado') return 'gray';
  if (status === 'recebido' || status === 'em_validacao') return 'blue';
  return 'amber';
}

export const ACCOUNT_TYPE_OPTIONS = [
  { value: 'corrente', label: 'Conta corrente' },
  { value: 'poupanca', label: 'Poupança' },
  { value: 'salario', label: 'Conta salário' },
  { value: 'pagamento', label: 'Conta de pagamento' },
];

export const BANK_ACCOUNT_STATUS_LABELS: Record<string, string> = {
  pendente_validacao: 'Pendente de validação',
  validada: 'Validada',
  rejeitada: 'Rejeitada',
  encerrada: 'Encerrada',
};

export const ADDRESS_STATUS_LABELS: Record<string, string> = {
  pendente: 'Aguardando aprovação',
  vigente: 'Vigente',
  encerrado: 'Encerrado',
  rejeitado: 'Rejeitado',
};

export const CONTACT_KIND_LABELS: Record<string, string> = {
  telefone: 'Telefone',
  whatsapp: 'WhatsApp',
  email_pessoal: 'E-mail pessoal',
};

/** `01310100` → `01310-100`. */
export function formatCep(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

/** Agência/conta com dígito: `1234` + `5` → `1234-5`. */
export function withDigit(main: string, digit: string | null | undefined): string {
  return digit ? `${main}-${digit}` : main;
}

/** Tamanho legível: 1536 → `1,5 KB`. */
export function formatBytes(size: number | null | undefined): string {
  if (!size) return '—';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1).replace('.', ',')} KB`;
  return `${(size / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}

// --- Vida funcional (change `pessoas-vida-funcional`) ---

export const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  lotacao_inicial: 'Lotação inicial',
  promocao: 'Promoção',
  alteracao_cargo: 'Alteração de cargo',
  alteracao_salarial: 'Alteração salarial',
  transferencia_area: 'Transferência de área/localidade',
  transferencia_centro_custo: 'Transferência de centro de custo',
  mudanca_gestor: 'Mudança de gestor',
  mudanca_regime_trabalho: 'Mudança de regime de trabalho',
  mudanca_tipo_contrato: 'Mudança de tipo de contrato',
  afastamento: 'Afastamento',
  retorno_afastamento: 'Retorno de afastamento',
  desligamento: 'Desligamento',
  reintegracao: 'Reintegração',
};

export const MOVEMENT_STATUS_LABELS: Record<string, string> = {
  agendada: 'Agendada',
  aguardando_aprovacao: 'Aguardando aprovação',
  efetivada: 'Efetivada',
  cancelada: 'Cancelada',
};

export const WORK_MODE_OPTIONS = [
  { value: 'presencial', label: 'Presencial' },
  { value: 'hibrido', label: 'Híbrido' },
  { value: 'remoto', label: 'Remoto' },
];

/** Campos de cada tipo no formulário de movimentação (espelha `movement-rules.ts`). */
export const MOVEMENT_FIELDS: Record<string, { fields: string[]; required: string[] }> = {
  lotacao_inicial: {
    fields: ['orgUnitId', 'jobPositionId', 'costCenterId', 'locationId', 'managerUserId', 'workMode'],
    required: ['orgUnitId', 'jobPositionId', 'workMode'],
  },
  promocao: { fields: ['jobPositionId', 'salaryAmount'], required: ['jobPositionId'] },
  alteracao_cargo: { fields: ['jobPositionId'], required: ['jobPositionId'] },
  alteracao_salarial: { fields: ['salaryAmount'], required: ['salaryAmount'] },
  transferencia_area: { fields: ['orgUnitId', 'locationId'], required: [] },
  transferencia_centro_custo: { fields: ['costCenterId'], required: ['costCenterId'] },
  mudanca_gestor: { fields: ['managerUserId'], required: ['managerUserId'] },
  mudanca_regime_trabalho: { fields: ['workMode'], required: ['workMode'] },
  mudanca_tipo_contrato: {
    fields: ['contractKind', 'contractEndDate', 'probationEndDate', 'probationExtensionEndDate'],
    required: ['contractKind'],
  },
};

/** Tipos que o usuário lança; os demais são gerados pelos processos. */
export function manualMovementTypes(hasAssignment: boolean): string[] {
  return Object.keys(MOVEMENT_FIELDS).filter((t) => (hasAssignment ? t !== 'lotacao_inicial' : t === 'lotacao_inicial'
    || t === 'alteracao_salarial' || t === 'mudanca_tipo_contrato'));
}

/** Transições manuais de situação (espelha `bond-status-machine.ts`). */
const MANUAL: Record<string, string[]> = {
  ativo: ['em_treinamento', 'suspenso', 'licenca_nao_remunerada', 'falecido'],
  em_treinamento: ['ativo', 'falecido'],
  suspenso: ['ativo', 'falecido'],
  licenca_nao_remunerada: ['ativo', 'falecido'],
  em_experiencia: ['ativo', 'falecido'],
  afastado: ['falecido'],
  licenciado: ['falecido'],
  ferias: ['falecido'],
  aviso_previo: ['falecido'],
  licenca_medica: ['falecido'],
  licenca_maternidade: ['falecido'],
  licenca_paternidade: ['falecido'],
};

export function manualStatusTargets(current: string): string[] {
  return MANUAL[current] ?? [];
}

/** Salário com efeito em mês anterior ao corrente vai para aprovação. */
export function isRetroactiveSalary(type: string, effectiveDate: string, today: string): boolean {
  return (type === 'alteracao_salarial' || type === 'promocao') && !!effectiveDate && effectiveDate.slice(0, 7) < today.slice(0, 7);
}

// --- Afastamentos (change `pessoas-afastamentos`) ---

export const LEAVE_STATUS_LABELS: Record<string, string> = {
  aguardando_aprovacao: 'Aguardando aprovação',
  agendado: 'Agendado',
  ativo: 'Em andamento',
  encerrado: 'Encerrado',
  cancelado: 'Cancelado',
  rejeitado: 'Rejeitado',
};

export const LEAVE_TARGET_OPTIONS = [
  { value: 'afastado', label: 'Afastado' },
  { value: 'licenciado', label: 'Licenciado' },
  { value: 'licenca_medica', label: 'Licença Médica' },
  { value: 'licenca_maternidade', label: 'Licença Maternidade' },
  { value: 'licenca_paternidade', label: 'Licença Paternidade' },
  { value: 'licenca_nao_remunerada', label: 'Licença Não Remunerada' },
  { value: 'suspenso', label: 'Suspenso' },
];

/** CID-10 (`J06`, `J06.9`), igual à validação do backend. */
export function isValidCid(value: string): boolean {
  return /^[A-Z][0-9]{2}(\.[0-9])?$/.test(value.trim().toUpperCase());
}

/** Dias corridos do afastamento, contando início e fim. */
export function leaveDays(start: string, end: string): number {
  return Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86_400_000) + 1;
}

// --- Desligamento e reintegração (change `pessoas-desligamento-reintegracao`) ---

export const TERMINATION_TYPE_OPTIONS = [
  { value: 'sem_justa_causa', label: 'Dispensa sem justa causa', clt: true },
  { value: 'justa_causa', label: 'Dispensa por justa causa', clt: true },
  { value: 'pedido_demissao', label: 'Pedido de demissão', clt: true },
  { value: 'acordo', label: 'Acordo entre as partes', clt: true },
  { value: 'termino_contrato', label: 'Término de contrato', clt: true },
  { value: 'termino_experiencia', label: 'Término de experiência', clt: true },
  { value: 'encerramento_contrato_pj', label: 'Encerramento de contrato PJ', clt: false },
];

export const NOTICE_OPTIONS = [
  { value: 'trabalhado', label: 'Trabalhado' },
  { value: 'indenizado', label: 'Indenizado' },
  { value: 'dispensado', label: 'Dispensado' },
  { value: 'nao_aplicavel', label: 'Não se aplica' },
];

export const TERMINATION_STATUS_LABELS: Record<string, string> = {
  em_andamento: 'Em andamento',
  concluido: 'Concluído',
  cancelado: 'Cancelado',
};

export const REINSTATEMENT_STATUS_LABELS: Record<string, string> = {
  aguardando_aprovacao: 'Aguardando aprovação',
  complementacao_solicitada: 'Complementação solicitada',
  aprovada: 'Aprovada',
  rejeitada: 'Rejeitada',
  cancelada: 'Cancelada',
};

export const REINSTATEMENT_STEP_LABELS: Record<string, string> = { rh: 'RH/gestor', juridico: 'Jurídico' };

/** Tipos de desligamento permitidos pelo tipo de vínculo. */
export function terminationTypesFor(employmentType: string) {
  return TERMINATION_TYPE_OPTIONS.filter((o) => (employmentType === 'pj' ? !o.clt : o.clt));
}

/** Progresso das tarefas obrigatórias (resolvidas / total). */
export function requiredProgress(tasks: Array<{ required: boolean; status: string }>): [number, number] {
  const req = tasks.filter((t) => t.required);
  return [req.filter((t) => t.status !== 'pendente').length, req.length];
}

// ------------------------------------------------------------------ férias

export const PERIOD_STATUS_LABELS: Record<string, string> = {
  em_aquisicao: 'Em aquisição',
  disponivel: 'Disponível',
  parcialmente_utilizado: 'Parcialmente utilizado',
  programado: 'Programado',
  em_gozo: 'Em gozo',
  concluido: 'Concluído',
  vencido: 'Vencido',
  perdido: 'Perdido',
  encerrado: 'Encerrado',
};

export const VACATION_REQUEST_STATUS_LABELS: Record<string, string> = {
  aguardando_aprovacao: 'Aguardando aprovação',
  devolvida: 'Devolvida para ajuste',
  aprovada: 'Aprovada',
  em_gozo: 'Em gozo',
  concluida: 'Concluída',
  rejeitada: 'Reprovada',
  cancelada: 'Cancelada',
  reprogramada: 'Reprogramada',
};

export const INSTALLMENT_STATUS_LABELS: Record<string, string> = {
  pendente: 'Pendente',
  programada: 'Programada',
  em_gozo: 'Em gozo',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
};

export const VACATION_STEP_LABELS: Record<string, string> = { gestor: 'Gestor', rh: 'RH' };

export const VACATION_DECISION_LABELS: Record<string, string> = { aprovado: 'Aprovado', rejeitado: 'Reprovado', devolvido: 'Devolvido' };

export const VACATION_ORIGIN_LABELS: Record<string, string> = { manual: 'Manual', workflow: 'Workflow', automatico: 'Automático' };

export const VACATION_EFFECT_OPTIONS = [
  { value: 'nenhum', label: 'Nenhum' },
  { value: 'suspende', label: 'Suspende a contagem do período' },
  { value: 'perde_6_meses', label: 'Perde o período acima de 6 meses' },
  { value: 'perde_30_dias', label: 'Perde o período acima de 30 dias' },
];

export const CRITICALITY_LABELS: Record<string, string> = {
  informativo: 'Informativo', atencao: 'Atenção', alto: 'Alto', critico: 'Crítico',
};

export function criticalityTone(level: string | null): string {
  switch (level) {
    case 'critico': return 'bg-red-100 text-red-800';
    case 'alto': return 'bg-orange-100 text-orange-800';
    case 'atencao': return 'bg-yellow-100 text-yellow-800';
    case 'informativo': return 'bg-blue-50 text-blue-800';
    default: return 'bg-gray-100 text-gray-700';
  }
}

/** Dias corridos de `start` a `end`, inclusive; 0 se incompleto ou invertido. */
export function vacationDays(start: string, end: string): number {
  if (!start || !end || end < start) return 0;
  return Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86_400_000) + 1;
}

/** Fim da parcela dado o início e a quantidade de dias corridos. */
export function vacationEnd(start: string, days: number): string {
  if (!start || days < 1) return '';
  const d = new Date(`${start}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days - 1);
  return d.toISOString().slice(0, 10);
}

/** Checagem local antes da pré-visualização: o servidor é quem decide. */
export function vacationFormErrors(
  installments: Array<{ startDate: string; days: number }>, sellDays: number, available: number, maxInstallments: number,
): string[] {
  const errors: string[] = [];
  const filled = installments.filter((i) => i.startDate && i.days > 0);
  if (filled.length === 0) errors.push('Informe ao menos uma parcela.');
  if (filled.length > maxInstallments) errors.push(`A política admite no máximo ${maxInstallments} parcela(s).`);
  if (filled.some((i) => i.days < 5)) errors.push('Cada parcela deve ter no mínimo 5 dias.');
  const total = filled.reduce((s, i) => s + i.days, 0) + sellDays;
  if (total > available) errors.push(`Os dias solicitados (${total}) passam do saldo disponível (${available}).`);
  return errors;
}

// ------------------------------------------------- alocação e lado do cliente

export const ACCESS_TASK_KIND_LABELS: Record<string, string> = { suspensao: 'Suspender acessos', reativacao: 'Reativar acessos' };

export const ACCESS_TASK_STATUS_LABELS: Record<string, string> = {
  pendente: 'Pendente', concluida: 'Concluída', falha: 'Falha', excecao: 'Exceção autorizada', cancelada: 'Cancelada',
};

/** Cor da situação das férias mostrada ao gestor do cliente. */
export function clientVacationTone(status: string | null): string {
  switch (status) {
    case 'em_ferias': return 'bg-green-100 text-green-800';
    case 'aguardando_ciencia_cliente': return 'bg-amber-100 text-amber-800';
    case 'ferias_proximas': return 'bg-blue-100 text-blue-800';
    case 'ferias_aprovadas': return 'bg-blue-50 text-blue-800';
    case 'canceladas':
    case 'reprogramadas': return 'bg-gray-100 text-gray-600';
    default: return 'bg-gray-50 text-gray-700';
  }
}

/** Alocação vigente na data (fim nulo ou ainda não alcançado). */
export function isAllocationActive(a: { validFrom: string; validTo: string | null }, today: string): boolean {
  return a.validFrom <= today && (!a.validTo || a.validTo >= today);
}
